import { api } from './client';
import { mapCart, mapOrder, mapProduct, mapSettings, toRawAddress } from './mappers';
import type { RawCart, RawCheckoutResponse, RawOrder, RawProduct, RawSettings } from './storeApiTypes';
import type { Address, Product } from './models';

const STORE = '/wc/store/v1';
const ODRA = '/odra/v1';

/* ---------- catalogue (WooCommerce Store API) ---------- */

export async function fetchProducts(signal?: AbortSignal): Promise<Product[]> {
  const raw = await api.get<RawProduct[]>(`${STORE}/products`, {
    per_page: 100,
    orderby: 'menu_order',
    order: 'asc',
    category: import.meta.env.VITE_RAIL_CATEGORY || undefined,
  }, signal);
  return raw.map(mapProduct);
}

export async function fetchProductBySlug(slug: string, signal?: AbortSignal): Promise<Product | null> {
  const raw = await api.get<RawProduct[]>(`${STORE}/products`, { slug }, signal);
  return raw[0] ? mapProduct(raw[0]) : null;
}

/* ---------- site settings and orders (ODRA companion plugin) ---------- */

export const fetchSettings = async (signal?: AbortSignal) =>
  mapSettings(await api.get<RawSettings>(`${ODRA}/settings`, undefined, signal));

export const fetchOrder = async (id: number, key: string, signal?: AbortSignal) =>
  mapOrder(await api.get<RawOrder>(`${ODRA}/order/${id}`, { key }, signal));

/* ---------- cart (WooCommerce Store API) ---------- */

export const fetchCart = async () => mapCart(await api.get<RawCart>(`${STORE}/cart`));

export async function addItem(product: Product, sizeSlug: string, quantity: number) {
  const size = product.sizes.find((s) => s.slug === sizeSlug);
  const body = size?.variationId
    ? { id: size.variationId, quantity, variation: [{ attribute: product.sizeAttribute, value: sizeSlug }] }
    : { id: product.id, quantity };
  return mapCart(await api.post<RawCart>(`${STORE}/cart/add-item`, body));
}

export const updateItem = async (key: string, quantity: number) =>
  mapCart(await api.post<RawCart>(`${STORE}/cart/update-item`, { key, quantity }));

export const removeItem = async (key: string) =>
  mapCart(await api.post<RawCart>(`${STORE}/cart/remove-item`, { key }));

export const applyCoupon = async (code: string) =>
  mapCart(await api.post<RawCart>(`${STORE}/cart/apply-coupon`, { code }));

export const removeCoupon = async (code: string) =>
  mapCart(await api.post<RawCart>(`${STORE}/cart/remove-coupon`, { code }));

export const selectShippingRate = async (packageId: number | string, rateId: string) =>
  mapCart(await api.post<RawCart>(`${STORE}/cart/select-shipping-rate`, { package_id: packageId, rate_id: rateId }));

/** Send the address so WooCommerce can work out which shipping rates apply. */
export const updateCustomer = async (address: Address) =>
  mapCart(await api.post<RawCart>(`${STORE}/cart/update-customer`, {
    billing_address: toRawAddress(address, true),
    shipping_address: toRawAddress(address, false),
  }));

/* ---------- checkout (WooCommerce Store API) ---------- */

export interface CheckoutInput {
  address: Address;
  paymentMethod: string;
  note?: string;
}

export async function placeOrder(input: CheckoutInput) {
  return api.post<RawCheckoutResponse>(`${STORE}/checkout`, {
    billing_address: toRawAddress(input.address, true),
    shipping_address: toRawAddress(input.address, false),
    customer_note: input.note ?? '',
    payment_method: input.paymentMethod,
    payment_data: [],
  });
}
